// Shared period/date-range math for the filter bars.
//
// Before this module every piece of this lived in one of three places: private
// helpers inside DateRangePicker/MonthPicker, or `currentMonthRange()` copied
// verbatim into four page components. Everything here is pure and ISO-string
// in / ISO-string out (`YYYY-MM-DD`), matching the wire format the API expects
// — the DB's own `DD/MM/YYYY` text format never appears at this layer.
//
// Vocabulary used throughout:
//   ISO date  'YYYY-MM-DD'  — a single day, the wire format
//   ym        'YYYY-MM'     — a month, the format client/src/utils/dueDate.js speaks
//   monthKey  'YYYYMM'      — a month, the format supplier_approvals stores
//   slashYm   'MM/YYYY'     — a month, the format server/lib/monthWindow.js emits
// Those three month formats already coexisted with no named conversion between
// them; `ymToMonthKey` / `ymToSlashYm` below are that missing vocabulary.

import { formatDateShort } from './dateFormat';

// ── ISO ⇄ Date ────────────────────────────────────────────────────────────

export const toISO = (d) => {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const fromISO = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const today = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

// ── Hebrew month names ────────────────────────────────────────────────────
// Gregorian months, Hebrew names — deliberately literal rather than derived
// from date-fns or Intl. `Intl.DateTimeFormat('he-IL-u-ca-hebrew')` would yield
// תשרי/חשוון, the *Hebrew calendar*, which is for holidays and not for
// accounting periods. Keeping these as literals also leaves the month grid free
// of any date library, which is what makes it cheap to unit-test.
export const HE_MONTHS = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];

// ── Month → range ─────────────────────────────────────────────────────────

// Last calendar day of a month, as a number. `new Date(y, monthIdx + 1, 0)`
// rolls back one day from the 1st of the next month, so leap years are handled
// by the platform rather than by us.
const lastDayOf = (year, monthIdx) => new Date(year, monthIdx + 1, 0).getDate();

// A whole calendar month as an ISO range. monthIdx is 0-based (0 = January).
export function monthRange(year, monthIdx) {
  const y = year + Math.floor(monthIdx / 12);
  const m = ((monthIdx % 12) + 12) % 12;
  const mm = String(m + 1).padStart(2, '0');
  const dd = String(lastDayOf(y, m)).padStart(2, '0');
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${dd}` };
}

export function currentMonthRange() {
  const d = new Date();
  return monthRange(d.getFullYear(), d.getMonth());
}

// The last N calendar months *including the current one*, month-aligned:
// from = 1st of (current − N + 1), to = last day of the current month.
// So lastNMonthsRange(1) is exactly currentMonthRange().
//
// Month-aligned by construction, which is what makes these presets safe on
// /suppliers — see server/lib/monthKey.js for why a non-aligned range there
// can't be scoped to an approval month.
export function lastNMonthsRange(n) {
  const d = new Date();
  const start = monthRange(d.getFullYear(), d.getMonth() - (n - 1));
  const end = monthRange(d.getFullYear(), d.getMonth());
  return { from: start.from, to: end.to };
}

export function previousMonthRange() {
  const d = new Date();
  return monthRange(d.getFullYear(), d.getMonth() - 1);
}

// The whole current calendar year: 1 בינואר – 31 בדצמבר. Month-aligned by
// construction (January's 1st to December's last day), so it is safe on
// /suppliers alongside the other PRESETS_MONTHLY entries, and — like them —
// it still matches itself tomorrow, so the rail highlight doesn't go stale.
export function currentYearRange() {
  const y = new Date().getFullYear();
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

// The year a range covers, when it covers exactly that whole calendar year.
// Null otherwise. Used for labelling; kept next to currentYearRange so the two
// definitions of "a year" can't drift apart.
export function deriveFullYear(from, to) {
  const span = deriveMonthSpan(from, to);
  if (!span) return null;
  const y = span.startYm.slice(0, 4);
  return span.startYm === `${y}-01` && span.endYm === `${y}-12` ? y : null;
}

// ── Range → month ─────────────────────────────────────────────────────────

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

// Does this range start on the 1st of a month and end on the last day of a
// (possibly different) month? Returns { startYm, endYm } or null.
export function deriveMonthSpan(from, to) {
  if (!from || !to) return null;
  const f = String(from).match(ISO_RE);
  const t = String(to).match(ISO_RE);
  if (!f || !t) return null;
  if (f[3] !== '01') return null;                                   // must start on the 1st
  const endLast = String(lastDayOf(Number(t[1]), Number(t[2]) - 1)).padStart(2, '0');
  if (t[3] !== endLast) return null;                                // must end on the last day
  if (`${f[1]}-${f[2]}` > `${t[1]}-${t[2]}`) return null;           // inverted
  return { startYm: `${f[1]}-${f[2]}`, endYm: `${t[1]}-${t[2]}` };
}

// 'YYYY-MM' when the range spans exactly one calendar month, else ''.
// Lifted verbatim in behaviour from MonthPicker's private `deriveMonth` —
// that part of MonthPicker was correct and is the one piece worth keeping.
export function deriveMonthKey(from, to) {
  const span = deriveMonthSpan(from, to);
  return span && span.startYm === span.endYm ? span.startYm : '';
}

export const isMonthAligned = (from, to) => deriveMonthSpan(from, to) !== null;

// Shift a range by its own length in months. `dir` is -1 (earlier) or +1.
// Returns null when the range doesn't denote whole months, which is the same
// set of cases deriveMonthSpan rejects — arbitrary day ranges, one-sided
// ranges and no range at all. There is no sensible "next month" for those, so
// the caller disables stepping rather than inventing one.
//
// Shifting by the span's OWN length (not always one month) keeps successive
// steps non-overlapping: פברואר–יולי stepped back becomes אוגוסט–ינואר, so
// repeated taps sweep history cleanly instead of crawling a month at a time.
// A single month is just the N=1 case, which is the common one — moving
// between adjacent months is the most repeated gesture in the product, because
// שוטף terms put an invoice's month and its payment's month 1–4 months apart.
export function stepRange(from, to, dir) {
  const span = deriveMonthSpan(from, to);
  if (!span) return null;
  const sY = Number(span.startYm.slice(0, 4));
  const sM = Number(span.startYm.slice(5)) - 1;
  const eY = Number(span.endYm.slice(0, 4));
  const eM = Number(span.endYm.slice(5)) - 1;
  const months = (eY * 12 + eM) - (sY * 12 + sM) + 1;
  const shift = dir * months;
  // monthRange normalises out-of-range month indices across year boundaries.
  const start = monthRange(sY, sM + shift);
  const end = monthRange(eY, eM + shift);
  return { from: start.from, to: end.to };
}

// ── Month-format conversions ──────────────────────────────────────────────

const ISO_MONTH_RE = /^\d{4}-\d{2}$/;

export const ymToMonthKey = (ym) => (ISO_MONTH_RE.test(ym) ? ym.replace('-', '') : '');
export const ymToSlashYm = (ym) => (ISO_MONTH_RE.test(ym) ? `${ym.slice(5)}/${ym.slice(0, 4)}` : '');

// ── Labels ────────────────────────────────────────────────────────────────

// Bidi isolation for a run of Latin digits/slashes sitting inside RTL text.
// U+2068 FIRST STRONG ISOLATE … U+2069 POP DIRECTIONAL ISOLATE is the
// character-level equivalent of <bdi>. Without it, `12/06/26 – 30/06/26`
// inside `direction: rtl` lets the bidi algorithm reorder the two date groups
// around the dash, so the range reads backwards. Using the control characters
// rather than markup means the same string is safe in an `aria-label` or
// `title` attribute, where an element can't go.
const iso = (s) => `⁨${s}⁩`;

const heMonthYear = (ym) => `${HE_MONTHS[Number(ym.slice(5)) - 1]} ${ym.slice(0, 4)}`;

// Human label for a range. Month naming outranks day naming: `יוני 2026` is
// unambiguous to a bookkeeper, and unlike a preset name ("חודש קודם") it does
// not change meaning overnight.
export function periodLabel(from, to) {
  if (!from && !to) return 'כל הזמן';

  const span = deriveMonthSpan(from, to);
  if (span) {
    const { startYm, endYm } = span;
    if (startYm === endYm) return heMonthYear(startYm);
    // A whole calendar year gets named as one, not spelled out as
    // "ינואר – דצמבר 2026" — same reasoning as naming a single month.
    const fullYear = deriveFullYear(from, to);
    if (fullYear) return `שנת ${fullYear}`;
    if (startYm.slice(0, 4) === endYm.slice(0, 4)) {
      // Same year — name it once, at the end.
      return `${HE_MONTHS[Number(startYm.slice(5)) - 1]} – ${heMonthYear(endYm)}`;
    }
    return `${heMonthYear(startYm)} – ${heMonthYear(endYm)}`;
  }

  if (from && to) return `${iso(formatDateShort(from))} – ${iso(formatDateShort(to))}`;
  if (from) return `מ-${iso(formatDateShort(from))}`;
  return `עד ${iso(formatDateShort(to))}`;
}

// ── Presets ───────────────────────────────────────────────────────────────
// Shape: { key, label, range: () => ({ from, to }) } with ISO strings ('' = open).

export const minusDaysRange = (n) => () => {
  const t = today();
  const f = today(); f.setDate(f.getDate() - n);
  return { from: toISO(f), to: toISO(t) };
};

const minusMonthsRange = (n) => () => {
  const t = today();
  const f = today(); f.setMonth(f.getMonth() - n);
  return { from: toISO(f), to: toISO(t) };
};

// "שנה נוכחית (2026)" — /documents only, see PRESETS_DOCUMENTS below.
//
// Its LABEL depends on the clock, so it is a getter rather than a fixed string:
// a tab left open across New Year's Eve would otherwise keep advertising last
// year's number while `range()` (which is evaluated per click) returned the new
// one.
const thisYearPreset = () => ({
  key: 'ty',
  get label() { return `שנה נוכחית (${new Date().getFullYear()})`; },
  range: currentYearRange,
});

// NOTE: there is deliberately no "כל הזמן" preset. Every page opens on a real
// period and the filter bars exist to move between periods, not to switch the
// screen to an unbounded query. The *state* is still reachable — `נקה טווח`
// clears the range, and /suppliers' empty state offers "search all dates" — and
// `periodLabel('', '')` still renders 'כל הזמן' for it, so nothing that lands in
// that state renders blank.

// /suppliers + /suppliers/paid. Every entry is month-aligned, because the
// payment approval is filed per calendar month (server/lib/monthKey.js) — a
// rolling window names no month, so the approve action would be unavailable.
//
// Month-aligned presets have a second, quieter benefit: they go stale less
// often. `activePresetKey` matches by exact value, so a rolling "30 ימים
// אחרונים" computed yesterday stops matching today and the rail highlight
// silently clears; a calendar month still matches itself tomorrow — but not
// next month, which is why anything that SAVES a range stores the preset key
// (toStoredPeriod, below) rather than the dates.
//
// That second reason is why LinkDeliveryNotesModal uses this set too, despite
// having no server-side month scoping: the question it asks is "which calendar
// month does this חשבונית מרכזת cover", and its default range IS 'חודש קודם' —
// which has to exist as a preset here, or the picker would open highlighting
// nothing.
export const PRESETS_MONTHLY = [
  { key: 'cm', label: 'חודש נוכחי', range: currentMonthRange },
  { key: 'pm', label: 'חודש קודם', range: previousMonthRange },
  { key: '3m', label: '3 חודשים אחרונים', range: () => lastNMonthsRange(3) },
  { key: '12m', label: '12 חודשים אחרונים', range: () => lastNMonthsRange(12) },
];

// /documents, /analysis, /credits — no month semantics server-side, and a true
// rolling window is the right primitive for spend analysis.
export const PRESETS_ANALYTIC = [
  { key: 'cm', label: 'חודש נוכחי', range: currentMonthRange },
  { key: '30d', label: '30 ימים אחרונים', range: minusDaysRange(30) },
  { key: '90d', label: '90 ימים אחרונים', range: minusDaysRange(90) },
  { key: '6m', label: '6 חודשים אחרונים', range: minusMonthsRange(6) },
  { key: '12m', label: '12 חודשים אחרונים', range: minusMonthsRange(12) },
];

// /documents — the analytic set plus "שנה נוכחית". This is the one screen where
// a whole year is a normal thing to ask for: it lists documents, so a year is
// just a longer list. The pages that share PRESETS_ANALYTIC don't get it —
// /analysis and /credits lead with cards and totals, and a year of those
// answers a different question than the one their default period is framing.
//
// /suppliers deliberately doesn't get it either, and for a harder reason: the
// payment approval is filed per calendar month, so anything longer than one
// month arrives month_scoped:false — the approve action goes unavailable
// (server/lib/monthKey.js). Offering a year there is offering a period that
// disables the page's own workflow.
export const PRESETS_DOCUMENTS = [...PRESETS_ANALYTIC, thisYearPreset()];

// Which preset (if any) exactly produces the current range. Derived on every
// render and never stored: with one source of truth (dateFrom/dateTo) there is
// no way for a remembered "mode" to disagree with the range actually applied,
// and clicking a day simply stops matching, clearing the highlight by itself.
export function activePresetKey(from, to, presets) {
  const f = from || '';
  const t = to || '';
  for (const p of presets) {
    const r = p.range();
    if ((r.from || '') === f && (r.to || '') === t) return p.key;
  }
  return null;
}

// ── Remembering a period ──────────────────────────────────────────────────
// Anything that SAVES a range — sessionStorage, the URL — saves a preset as its
// KEY, never as the dates it happened to produce. The dates are only true on
// the day they were computed: a "12 חודשים אחרונים" saved on 16/09 and restored
// on 28/09 still ended on 16/09, so every credit opened since was silently off
// screen (reported from the field, 28/09/2026). Month-aligned presets freeze the
// same way, just less often — "חודש נוכחי" saved on 30/09 still means September
// on 02/10. A range the user picked day by day is the user's explicit choice
// and is kept verbatim.
//
// Both writers take the preset KEY from the caller rather than re-deriving it
// here: deciding "is this a preset?" at write time compares a mounted page's
// yesterday dates with today's preset and answers no, re-freezing the range.
// Pages get the key from usePresetPeriod, which fixes it when the range
// changes; a one-off link builder whose range was computed just now can pass
// activePresetKey(from, to, presets) directly.

// { preset: key } when a preset is active, else { from, to } ('' = open side).
export function toStoredPeriod(presetKey, from, to) {
  return presetKey ? { preset: presetKey } : { from: from || '', to: to || '' };
}

// Back to { from, to }, re-evaluating a preset against today. Null for nothing
// stored, an unknown preset key, or malformed dates — callers fall back to
// their own default.
export function fromStoredPeriod(stored, presets) {
  if (!stored || typeof stored !== 'object') return null;
  if (stored.preset) {
    const p = presets.find(x => x.key === stored.preset);
    return p ? p.range() : null;
  }
  const ok = (s) => s === '' || ISO_RE.test(s);
  if (typeof stored.from !== 'string' || typeof stored.to !== 'string') return null;
  return ok(stored.from) && ok(stored.to) ? { from: stored.from, to: stored.to } : null;
}

// URL form of the same rule: `?period=90d` for a preset, `?from=&to=` otherwise.
// Returns a NEW URLSearchParams carrying every other param of `params` as-is.
export function withPeriodParams(params, presetKey, from, to) {
  const next = new URLSearchParams(params);
  next.delete('period');
  next.delete('from');
  next.delete('to');
  if (presetKey) {
    next.set('period', presetKey);
  } else {
    if (from) next.set('from', from);
    if (to) next.set('to', to);
  }
  return next;
}

// { from, to } from a URL: a known `period` key re-evaluated against today,
// else the literal `from`/`to` (null when absent) — so links and bookmarks
// written before `period` existed keep working unchanged.
export function readPeriodParams(params, presets) {
  const p = presets.find(x => x.key === params.get('period'));
  if (p) return p.range();
  return { from: params.get('from') || null, to: params.get('to') || null };
}
